import { useState } from 'react'
import {
  Text,
  BorderRadius6,
  GreyGrey0,
  GreyGrey70,
  GreyWhite,
  Primary13Regular,
  ShadowM,
  Skeleton,
  Box,
  Flex
} from '@sequencehq/core-components'
import {
  InvoiceRendererData,
  ReactInvoiceRenderer
} from '@sequencehq/invoice-content'
import { SourceEmailHeader } from 'Automations/watchtower/inbox/manualTasks/components'
import { InvoiceReviewFormValues, InvoiceUpdateReviewSource } from '../../types'
import { useInvoiceReviewRendererData } from '../../hooks/useInvoiceReviewRendererData'

// The invoice document renders at a fixed width and is centred in the pane, which
// flexes to fill the space left of the form. Matches the invoice editor's preview
// page width band.
const DOCUMENT_PREVIEW_WIDTH = 520

/**
 * Left ("preview") pane of the review modal: the live invoice document, rendered
 * with the same `ReactInvoiceRenderer` the invoice editor uses. Driven by the
 * form's current values, so the rendered "To" address, PO number, reference and
 * memo update live as the operator edits — the invoice-side equivalent of the
 * customer-info review's before/after cards.
 *
 * When the change came from a forwarded email, a collapsible subheading at the
 * top attributes it to that email and expands to show the original subject and
 * body (see `SourceEmailHeader`).
 */
export const InvoiceDocumentPreview = ({
  invoiceId,
  editedValues,
  source
}: {
  invoiceId: string
  editedValues: InvoiceReviewFormValues
  source?: InvoiceUpdateReviewSource
}) => {
  const { rendererData, isError } = useInvoiceReviewRendererData({
    invoiceId,
    editedValues
  })

  // While the forwarded email is expanded the document below is dimmed, so the
  // operator's attention stays on the email. It stays scrollable — the dim is
  // purely visual.
  const [isSourceExpanded, setIsSourceExpanded] = useState(false)

  return (
    <Flex
      flexDirection="column"
      height="100%"
      width="100%"
      overflow="hidden"
      backgroundColor={GreyGrey0}
    >
      {source && (
        <Box flexShrink={0}>
          <SourceEmailHeader
            source={source}
            onExpandedChange={setIsSourceExpanded}
            data-testid="invoiceUpdateReview.sourceHeader"
          />
        </Box>
      )}

      <Box
        flex="1"
        overflow="auto"
        padding="24px"
        opacity={isSourceExpanded ? 0.4 : 1}
        transition="opacity 0.2s ease-in-out"
        data-testid="invoiceUpdateReview.documentPreview"
      >
        <Flex minHeight="100%" justifyContent="center" alignItems="center">
          <DocumentContent isError={isError} rendererData={rendererData} />
        </Flex>
      </Box>
    </Flex>
  )
}

/**
 * The document area's three states: an error message when the invoice can't be
 * loaded, the invoice page rendering skeletons while its data loads, or the live
 * rendered invoice once ready.
 */
const DocumentContent = ({
  isError,
  rendererData
}: {
  isError: boolean
  rendererData: InvoiceRendererData | undefined
}) => {
  if (isError) {
    return (
      <Text
        {...Primary13Regular}
        color={GreyGrey70}
        data-testid="invoiceUpdateReview.documentPreview.unavailable"
      >
        Invoice preview unavailable
      </Text>
    )
  }

  return (
    <Box
      width={`${DOCUMENT_PREVIEW_WIDTH}px`}
      height="fit-content"
      borderRadius={BorderRadius6}
      border="1px solid rgba(0, 0, 0, 0.08)"
      boxShadow={ShadowM}
      background={GreyWhite}
      overflow="hidden"
    >
      {rendererData ? (
        <ReactInvoiceRenderer
          data={rendererData}
          theme="DEFAULT"
          width={DOCUMENT_PREVIEW_WIDTH}
          showSkeletons={false}
        />
      ) : (
        <Skeleton height="640px" width="100%" />
      )}
    </Box>
  )
}
